import { useState } from 'react';
import { useParams } from 'react-router-dom';

import styled, { css, keyframes, useTheme } from 'styled-components';
import {
	CommentCategory,
	ICreatorContentsDB,
	ICreatorContentsDetailsDB,
} from 'type/wconcepdb';

import BasicColumnChart from 'components/common/chart/BasicColumnChart';
import BasicDonutChart from 'components/common/chart/BasicDonutChart';
import HorizontalBarGraph from 'components/common/chart/HorizontalBarGraph';
import {
	useGetCreatorContentsDetails,
	useGetCreatorProfile,
} from 'hooks/queries/creator';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreTooltip from 'shared/CoreTooltip';
import {
	EllipsisText,
	ReportSectionCard,
	ReportSectionText,
	ReportColorCircle,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

import ContentCard from './ContentCard';
import ContentKeyword from './ContentKeyword';

const ad_comment_type = ['광고 관련 질문', '광고 정보 획득', '정보 활용 의지'];
const common_comment_type = ['인플루언서 소통', '콘텐츠 피드백', '기타'];
const comment_type = ['광고 관련 소재', '일반 댓글'];

const graph_arr = [
	'인게이지먼트',
	'좋아요 수',
	'댓글 수',
	'조회 수',
	'클릭 수(소셜비즈)',
];

const dataMapper: {
	[key: string]: keyof typeof mockReportData.reportGraph;
} = {
	인게이지먼트: 'engagement',
	'좋아요 수': 'likeCount',
	'댓글 수': 'commentCount',
	'조회 수': 'viewCount',
	'클릭 수(소셜비즈)': 'clickCount',
} as const;

const ContentReport = ({
	contentIdx,
	contentPk,
}: {
	contentIdx: number;
	contentPk: number;
}) => {
	const theme = useTheme();

	const { platform, recommendationId, creatorPk } = useParams();

	const { data: creatorProfile, isLoading: creatorProfileLoading } = useGetCreatorProfile(
		recommendationId ?? '',
		creatorPk ?? '',
	);

	const { data: creatorContentsDetails, isLoading: creatorContentsDetailsLoading } =
		useGetCreatorContentsDetails(
			recommendationId ?? '',
			creatorPk ?? '',
			String(contentPk),
		);

	const selectedContent =
		creatorProfile?.contents.find((content) => content.contentPk === contentPk) ??
		({} as ICreatorContentsDB);

	const selectedContentReport = creatorContentsDetails;

	const [selectedChips, setSelectedChips] = useState('인게이지먼트');
	const [comment_type_chip, setCommentTypeChip] = useState<string>(comment_type[0]);

	const platformDataMapper = { ...dataMapper };
	if (platform === 'yt') {
		delete platformDataMapper['클릭 수(소셜비즈)'];
	}

	const selectedContentChartData =
		selectedContentReport?.reportGraph[platformDataMapper[selectedChips]];

	if (platform === 'yt') {
		delete selectedContentReport?.reportGraph.clickCount;
	}

	if (creatorContentsDetailsLoading || !selectedContentReport) return null;

	return (
		<>
			<Grid flex="start" _gap="16px">
				<ContentCard
					content={selectedContent}
					selected={false}
					_onClick={() => {
						if (selectedContent.link) window.open(selectedContent.link);
					}}
				/>

				<ReportSectionCard _height="411px">
					<Grid flex="between" _margin="0 0 16px 0">
						<CoreRadioChips
							group_type="carousel"
							style_type="default"
							_width="max-content"
							chips_list={Object.keys(platformDataMapper)}
							selected_chips={selectedChips}
							setSelectedChips={setSelectedChips}
						/>
					</Grid>
					<Grid flex="start" _height="100%" _gap="16px">
						<BasicColumnChart
							width="742px"
							height="300px"
							data_name={selectedChips}
							dataLabels={false}
							datas={selectedContentChartData?.map((item) => item.value) ?? []}
							categorys={selectedContentChartData?.map((item) => item.date) ?? []}
							zoom={false}
							trend={1}
							xTickAmount={3}
						/>
					</Grid>
				</ReportSectionCard>
			</Grid>

			<Grid>
				<ReportSectionCard>
					<ReportSectionText typo="span" _margin="0 0 8px 0">
						URL별 클릭 수
					</ReportSectionText>
					<Grid flex="between" direction="column" flex_option="2 1 0" _gap="8px">
						{selectedContentReport?.clickCountsByUrl ? (
							selectedContentReport?.clickCountsByUrl?.slice(0, 3)?.map((data, idx) => (
								<Grid key={idx} flex="start" _gap="24px">
									<Grid _width="160px" flex="start" _gap="16px">
										<Text
											_width="9px"
											typo="p_bold"
											color={theme.colors.gray.gray80_text}
										>
											{idx + 1}
										</Text>
										<Grid flex="between" _gap="8px">
											<Grid flex="start" _gap="4px">
												<EllipsisText typo="span">클릭 수</EllipsisText>
											</Grid>
											<EllipsisText _width="100%" typo="span">
												{data.value}회
											</EllipsisText>
										</Grid>
									</Grid>
									<KeywordBox flex="start" _height="100%">
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
											underline
										>
											<a
												href={selectedContentReport?.clickCountsByUrl?.[idx]?.url}
												target="_blank"
												rel="noopener noreferrer"
											>
												&nbsp;{selectedContentReport?.clickCountsByUrl?.[idx]?.url}
											</a>
										</Text>
									</KeywordBox>
								</Grid>
							))
						) : (
							<>
								<KeywordBox>
									<Text
										color={theme.colors.gray.gray80_text}
										white_space="normal"
										typo="p_bold"
									>
										&nbsp; 분석가능한 URL이 없습니다.
									</Text>
								</KeywordBox>

								<KeywordBox>
									<Text
										color={theme.colors.gray.gray80_text}
										white_space="normal"
										typo="p_bold"
									>
										&nbsp; 분석가능한 URL이 없습니다.
									</Text>
								</KeywordBox>
								<KeywordBox>
									<Text
										color={theme.colors.gray.gray80_text}
										white_space="normal"
										typo="p_bold"
									>
										&nbsp; 분석가능한 URL이 없습니다.
									</Text>
								</KeywordBox>
							</>
						)}
					</Grid>
				</ReportSectionCard>
			</Grid>
			<Grid flex="start" _gap="16px">
				<ReportSectionCard _width="max-content" _height="284px">
					<Grid _width="200px" flex="between">
						<ReportSectionText typo="span" _margin="0 0 8px 0">
							전체 댓글 비율
						</ReportSectionText>
					</Grid>
					<Grid flex="start" _gap="4px">
						<ReportColorCircle color_type="primary" />
						<ReportSectionText typo="p_bold">광고 관련 댓글</ReportSectionText>
					</Grid>
					<Grid flex="start" _gap="4px">
						<ReportColorCircle color_type="primary_darken" />
						<ReportSectionText typo="p_bold">일반 댓글</ReportSectionText>
					</Grid>

					<Grid _width="100%">
						<BasicDonutChart
							value={[
								Math.round(
									selectedContentReport?.overallCommentRate?.commentCategories?.[0]
										?.percentage || 0,
								),
								Math.round(
									selectedContentReport?.overallCommentRate?.commentCategories?.[1]
										?.percentage || 0,
								),
							]}
							max_label={true}
							unit="%"
							legend_show={false}
							value_name={['광고 관련 댓글', '일반 댓글']}
						/>
						{platform === 'ig' && (
							<Grid flex="center">
								<Text typo="span" color={theme.colors.gray.gray80_text}>
									{'*소셜비즈 키워드 제외'}
								</Text>
							</Grid>
						)}
					</Grid>
				</ReportSectionCard>
				<ReportSectionCard _height="284px">
					<Grid flex="between">
						<ReportSectionText typo="span" _margin="0 0 8px 0">
							댓글 주요 키워드
						</ReportSectionText>
					</Grid>
					<Grid _width="100%" _height="200px">
						{selectedContentReport?.topCommentKeywords ? (
							<ContentKeyword
								keyword={
									selectedContentReport?.topCommentKeywords?.map((item, idx) => ({
										text: item.keyword,
										value: item.score,
									})) ?? []
								}
							/>
						) : (
							<Text typo="span_bold">분석 가능한 댓글이 없습니다.</Text>
						)}
					</Grid>
				</ReportSectionCard>
			</Grid>
			<Grid flex="center" direction="column" _gap="10px">
				<ReportSectionCard>
					<Grid flex="between">
						<ReportSectionText typo="span" _margin="0 0 8px 0">
							댓글 주요 문장
						</ReportSectionText>
					</Grid>
					<Grid flex="start" _gap="16px" align_items="stretch">
						<KeywordBox flex_option="1 1 0">
							<ReportSectionText typo="span" _margin="0 0 8px 0">
								전체 댓글 비율
							</ReportSectionText>
							<CoreRadioChips
								group_type="carousel"
								style_type="default"
								chips_list={comment_type}
								_width="100%"
								selected_chips={comment_type_chip}
								setSelectedChips={setCommentTypeChip}
							/>
							<Grid _margin="16px 0 0 0">
								<CommentCategoryGraph
									selectedContentReport={selectedContentReport}
									type={
										comment_type_chip === '광고 관련 소재'
											? 'adRelatedComment'
											: 'normalComment'
									}
								/>
							</Grid>
						</KeywordBox>
						<Grid flex="between" direction="column" flex_option="2 1 0" _gap="8px">
							{selectedContentReport?.topCommentSentences ? (
								selectedContentReport?.topCommentSentences
									?.slice(0, 3)
									?.map((data, idx) => (
										<KeywordBox key={idx} flex="start" _height="100%">
											<Text
												color={theme.colors.gray.gray80_text}
												white_space="normal"
												typo="p_bold"
											>
												✏️&nbsp;{data}
											</Text>
										</KeywordBox>
									))
							) : (
								<>
									<KeywordBox>
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											✏️&nbsp; 분석가능한 댓글이 없습니다.
										</Text>
									</KeywordBox>

									<KeywordBox>
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											✏️&nbsp; 분석가능한 댓글이 없습니다.
										</Text>
									</KeywordBox>
									<KeywordBox>
										<Text
											color={theme.colors.gray.gray80_text}
											white_space="normal"
											typo="p_bold"
										>
											✏️&nbsp; 분석가능한 댓글이 없습니다.
										</Text>
									</KeywordBox>
								</>
							)}
						</Grid>
					</Grid>
				</ReportSectionCard>
				<Grid flex="end">
					{platform === 'ig' && (
						<Text typo="span" color={theme.colors.gray.gray80_text}>
							{'*댓글 분석은 인게이지먼트 상위 구간 댓글 기준으로 분석됩니다.'}
						</Text>
					)}
					{platform === 'yt' && (
						<Text typo="span" color={theme.colors.gray.gray80_text}>
							{'*댓글 분석은 최근 구간 댓글 기준으로 분석됩니다.'}
						</Text>
					)}
				</Grid>
			</Grid>
		</>
	);
};

interface CommentCategoryGraphProps {
	selectedContentReport: ICreatorContentsDetailsDB | undefined;
	type: 'adRelatedComment' | 'normalComment';
}

const CommentCategoryGraph = ({
	selectedContentReport,
	type,
}: CommentCategoryGraphProps) => {
	if (type === 'adRelatedComment') {
		const adCategory = selectedContentReport?.overallCommentRate?.commentCategories.find(
			(category) => category.type === 'adRelatedComment',
		);
		const formattedData =
			adCategory?.type === 'adRelatedComment'
				? {
						'광고 관련 질문': adCategory.categories.adQuestions,
						'광고 정보 획득': adCategory.categories.adInformation,
						'정보 활용 의지': adCategory.categories.adIntent,
				  }
				: {
						'광고 관련 질문': 0,
						'광고 정보 획득': 0,
						'정보 활용 의지': 0,
				  };

		return <HorizontalBarGraph data={formattedData} max={100} background={true} />;
	}

	const normalCategory =
		selectedContentReport?.overallCommentRate?.commentCategories.find(
			(category) => category.type === 'normalComment',
		);

	const formattedData =
		normalCategory?.type === 'normalComment'
			? {
					'인플루언서 소통': normalCategory.categories.influencerInteraction,
					'콘텐츠 피드백': normalCategory.categories.contentFeedback,
					기타: normalCategory.categories.others,
			  }
			: {
					'인플루언서 소통': 0,
					'콘텐츠 피드백': 0,
					기타: 0,
			  };

	return <HorizontalBarGraph data={formattedData} max={100} background={true} />;
};

const { Text, Grid } = style;

const post_ani = keyframes`
    0% {
      	opacity: 1;
    }
    50% {
		opacity: 0.5;
    }
    100% {
		opacity: 1;
    }
`;

const PostBox = styled.div<{ animation?: boolean; delay?: string }>`
	width: 200px;
	height: 200px;
	flex-shrink: 0;
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	overflow: hidden;
	position: relative;
	${({ animation }) =>
		animation &&
		css`
			animation: ${post_ani} 2s infinite ease-in-out;
		`}
	${({ delay }) => delay && `animation-delay: ${delay}`};
`;

const PostImg = styled.div<{ picture?: string; color?: string }>`
	position: relative;
	padding-top: 100%;
	overflow: hidden;
	background-color: '#f8f8f8';
	background: url(${({ picture }) => picture}) no-repeat center / cover,
		url('/image/small_no_image.png') no-repeat center / cover;
`;
const KeywordBox = styled(Grid)`
	padding: 8px 16px;
	border-radius: 8px;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray10};
	background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
`;

export default ContentReport;

const mockReportData: ICreatorContentsDetailsDB = {
	reportGraph: {
		engagement: [
			{ date: '2024-01-01', value: 65 },
			{ date: '2024-01-02', value: 55 },
			{ date: '2024-01-03', value: 45 },
			{ date: '2024-01-04', value: 75 },
			{ date: '2024-01-05', value: 95 },
		],
		likeCount: [
			{ date: '2024-01-01', value: 200 },
			{ date: '2024-01-02', value: 450 },
			{ date: '2024-01-03', value: 850 },
			{ date: '2024-01-04', value: 750 },
			{ date: '2024-01-05', value: 920 },
		],
		commentCount: [
			{ date: '2024-01-01', value: 30 },
			{ date: '2024-01-02', value: 45 },
			{ date: '2024-01-03', value: 55 },
			{ date: '2024-01-04', value: 180 },
			{ date: '2024-01-05', value: 280 },
		],
		viewCount: [
			{ date: '2024-01-01', value: 1200 },
			{ date: '2024-01-02', value: 1500 },
			{ date: '2024-01-03', value: 2200 },
			{ date: '2024-01-04', value: 3500 },
			{ date: '2024-01-05', value: 4800 },
		],
		clickCount: [
			{ date: '2024-01-01', value: 35 },
			{ date: '2024-01-02', value: 123 },
			{ date: '2024-01-03', value: 23 },
			{ date: '2024-01-04', value: 167 },
			{ date: '2024-01-05', value: 11 },
		],
	},
	clickCountsByUrl: [
		{ url: 'https://example.com/product1', value: 15 },
		{ url: 'https://example.com/product2', value: 25 },
		{ url: 'https://example.com/product3', value: 35 },
	],
	overallCommentRate: {
		totalPercentage: 100,
		commentCategories: [
			{
				type: 'adRelatedComment',
				percentage: 30,
				categories: {
					adQuestions: 50,
					adInformation: 30,
					adIntent: 20,
				},
			},
			{
				type: 'normalComment',
				percentage: 70,
				categories: {
					influencerInteraction: 40,
					contentFeedback: 30,
					others: 30,
				},
			},
		],
	},
	topCommentKeywords: [
		{ keyword: '좋아요', score: 120 },
		{ keyword: '최고에요', score: 80 },
		{ keyword: '유용해요', score: 70 },
		{ keyword: '퀄리티', score: 60 },
		{ keyword: '추천해요', score: 50 },
		{ keyword: '감사해요', score: 40 },
	],
	topCommentSentences: [
		'제품 설명이 너무 자세해서 좋아요!',
		'영상 퀄리티가 너무 좋네요.',
		'다음 콘텐츠도 기대됩니다.',
	],
};
